import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Link2,
  Rabbit,
  Turtle,
} from "lucide-react";

const LinkedListCycleVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Cycle at position 1",
      list: [3, 2, 0, -4],
      cyclePos: 1,
      expected: true,
    },
    {
      id: 2,
      name: "Cycle at position 0",
      list: [1, 2],
      cyclePos: 0,
      expected: true,
    },
    {
      id: 3,
      name: "No cycle",
      list: [1, 2, 3, 4],
      cyclePos: -1,
      expected: false,
    },
    {
      id: 4,
      name: "Single node with cycle",
      list: [1],
      cyclePos: 0,
      expected: true,
    },
    {
      id: 5,
      name: "Long cycle",
      list: [1, 2, 3, 4, 5, 6],
      cyclePos: 2,
      expected: true,
    },
    {
      id: 6,
      name: "Empty list",
      list: [],
      cyclePos: -1,
      expected: false,
    },
    {
      id: 7,
      name: "Cycle at last node",
      list: [1, 2, 3, 4, 5],
      cyclePos: 4,
      expected: true,
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [customCyclePos, setCustomCyclePos] = useState("");
  const [list, setList] = useState(testCases[0].list);
  const [cyclePos, setCyclePos] = useState(testCases[0].cyclePos);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);
  const [cycleDetected, setCycleDetected] = useState(false);

  // Floyd's Cycle Detection Algorithm
  const pseudoCode = [
    { line: "function hasCycle(head):", indent: 0 },
    { line: "if (!head || !head.next):", indent: 1 },
    { line: "return false", indent: 2 },
    { line: "", indent: 0 },
    { line: "slow = head      // Tortoise", indent: 1 },
    { line: "fast = head      // Hare", indent: 1 },
    { line: "", indent: 0 },
    { line: "while (fast && fast.next):", indent: 1 },
    { line: "slow = slow.next      // Move 1 step", indent: 2 },
    { line: "fast = fast.next.next // Move 2 steps", indent: 2 },
    { line: "", indent: 0 },
    { line: "if (slow === fast):", indent: 2 },
    { line: "return true  // Cycle detected!", indent: 3 },
    { line: "", indent: 0 },
    { line: "return false  // No cycle", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Node representation
  class Node {
    constructor(value, index) {
      this.value = value;
      this.index = index;
      this.next = null;
    }
  }

  // Create linked list with cycle
  const createLinkedListWithCycle = (arr, pos) => {
    if (arr.length === 0) return null;

    const nodes = arr.map((val, idx) => new Node(val, idx));

    // Connect nodes
    for (let i = 0; i < nodes.length - 1; i++) {
      nodes[i].next = nodes[i + 1];
    }

    // Create cycle if pos is valid
    if (pos >= 0 && pos < nodes.length) {
      nodes[nodes.length - 1].next = nodes[pos];
    }

    return nodes[0];
  };

  // Calculate all steps
  const calculateSteps = (arr, pos) => {
    if (arr.length === 0) {
      return [
        {
          iteration: 0,
          slowIndex: -1,
          fastIndex: -1,
          description: "Empty list - no cycle",
          highlightLine: 2,
          isFinal: true,
        },
      ];
    }

    const allSteps = [];
    const head = createLinkedListWithCycle(arr, pos);

    // Initial check
    allSteps.push({
      iteration: 0,
      slowIndex: 0,
      fastIndex: 0,
      visited: new Set([0]),
      description: "Initialize: slow and fast both at head",
      highlightLine: 4,
    });

    let slow = head;
    let fast = head;
    let iteration = 0;
    const visitedByFast = new Set([0]);
    const visitedBySlow = new Set([0]);

    while (fast && fast.next) {
      iteration++;

      // Move slow one step
      slow = slow.next;
      visitedBySlow.add(slow.index);

      // Move fast two steps
      fast = fast.next.next;
      if (fast) {
        visitedByFast.add(fast.index);
      }

      allSteps.push({
        iteration,
        slowIndex: slow.index,
        fastIndex: fast ? fast.index : -1,
        visitedBySlow: new Set(visitedBySlow),
        visitedByFast: new Set(visitedByFast),
        description: `Step ${iteration}: Slow at node ${slow.index}, Fast at ${
          fast ? `node ${fast.index}` : "null"
        }`,
        highlightLine: 8,
      });

      if (slow === fast) {
        allSteps.push({
          iteration: iteration + 1,
          slowIndex: slow.index,
          fastIndex: fast.index,
          visitedBySlow: new Set(visitedBySlow),
          visitedByFast: new Set(visitedByFast),
          description: `Cycle detected! Both pointers meet at node ${slow.index}`,
          highlightLine: 12,
          cycleDetected: true,
          isFinal: true,
        });
        return allSteps;
      }
    }

    allSteps.push({
      iteration: iteration + 1,
      slowIndex: slow ? slow.index : -1,
      fastIndex: -1,
      visitedBySlow: new Set(visitedBySlow),
      visitedByFast: new Set(visitedByFast),
      description: "No cycle detected - reached end of list",
      highlightLine: 14,
      cycleDetected: false,
      isFinal: true,
    });

    return allSteps;
  };

  // Initialize steps when test case changes
  useEffect(() => {
    const newSteps = calculateSteps(list, cyclePos);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
    setCycleDetected(false);
  }, [list, cyclePos]);

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || finished) return;

    const timer = setTimeout(() => {
      handleNextStep();
    }, speed);

    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      const step = steps[nextStep];
      setHighlightedLine(step.highlightLine);

      if (step.isFinal) {
        setFinished(true);
        setIsPlaying(false);
        if (step.cycleDetected) {
          setCycleDetected(true);
        }
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      const step = steps[prevStep];
      setHighlightedLine(step.highlightLine);
      setFinished(false);
      setCycleDetected(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
    setCycleDetected(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setList(testCases[index].list);
    setCyclePos(testCases[index].cyclePos);
  };

  const addCustomTestCase = () => {
    try {
      const cleanInput = customInput.trim().replace(/[\[\]]/g, "");
      const numbers = cleanInput.split(",").map((n) => parseInt(n.trim()));
      const pos = parseInt(customCyclePos) || -1;

      if (numbers.some(isNaN) || numbers.length === 0) {
        alert("Please enter valid numbers");
        return;
      }

      if (pos < -1 || pos >= numbers.length) {
        alert(`Cycle position must be between -1 and ${numbers.length - 1}`);
        return;
      }

      const newTestCase = {
        id: Date.now(),
        name: `Custom ${testCases.length - 6}`,
        list: numbers,
        cyclePos: pos,
        expected: pos >= 0,
      };

      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
      setCustomCyclePos("");
    } catch (error) {
      alert("Invalid input format");
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      const newTestCases = testCases.filter((tc) => tc.id !== id);
      setTestCases(newTestCases);
      if (selectedTestCase >= newTestCases.length) {
        handleTestCaseChange(0);
      }
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  // Create visual representation of the linked list
  const createVisualNodes = () => {
    const nodes = [];
    for (let i = 0; i < list.length; i++) {
      nodes.push({
        value: list[i],
        index: i,
        hasNext: i < list.length - 1 || cyclePos >= 0,
      });
    }
    return nodes;
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Link2 className="text-blue-500" size={24} />
        Linked List Cycle Detection - Floyd's Algorithm
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left side - Visualization */}
        <div className="space-y-3">
          {/* Test Cases */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-36 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      List: [{tc.list.join(", ")}]{" "}
                      {tc.cyclePos >= 0 ? `→ pos ${tc.cyclePos}` : ""}
                    </div>
                    <div className="text-xs opacity-75">
                      Expected: {tc.expected ? "Has Cycle" : "No Cycle"}
                    </div>
                  </button>
                  {tc.id > 7 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add custom test case */}
            <div className="mt-2 space-y-1">
              <div className="flex gap-1">
                <input
                  type="text"
                  value={customInput}
                  onChange={(e) => setCustomInput(e.target.value)}
                  placeholder="List: 3,2,0,-4"
                  className="flex-1 px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <input
                  type="number"
                  value={customCyclePos}
                  onChange={(e) => setCustomCyclePos(e.target.value)}
                  placeholder="Pos: 1"
                  className="w-16 px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} />
                Add Test Case
              </button>
            </div>
          </div>

          {/* Linked List Visualization */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            {/* Pointers Display */}
            <div className="mb-3 p-2 bg-gray-50 rounded">
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2">
                  <Turtle className="text-green-600" size={16} />
                  <div className="text-xs">
                    <span className="font-medium text-green-700">Slow: </span>
                    <span className="font-mono">
                      {currentStepData?.slowIndex >= 0
                        ? `Node ${currentStepData.slowIndex}`
                        : "null"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Rabbit className="text-purple-600" size={16} />
                  <div className="text-xs">
                    <span className="font-medium text-purple-700">Fast: </span>
                    <span className="font-mono">
                      {currentStepData?.fastIndex >= 0
                        ? `Node ${currentStepData.fastIndex}`
                        : "null"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Linked List Visual */}
            <div className="overflow-x-auto py-4">
              <div className="flex flex-wrap gap-2">
                {createVisualNodes().map((node, idx) => (
                  <div key={idx} className="relative">
                    <div
                      className={`w-12 h-12 flex items-center justify-center border-2 rounded transition-all duration-300 ${
                        idx === currentStepData?.slowIndex &&
                        idx === currentStepData?.fastIndex
                          ? "bg-red-200 border-red-500 scale-110 ring-2 ring-red-300"
                          : idx === currentStepData?.slowIndex
                          ? "bg-green-200 border-green-500 scale-110"
                          : idx === currentStepData?.fastIndex
                          ? "bg-purple-200 border-purple-500 scale-110"
                          : currentStepData?.visitedBySlow?.has(idx) ||
                            currentStepData?.visitedByFast?.has(idx)
                          ? "bg-blue-100 border-blue-400"
                          : "bg-gray-100 border-gray-300"
                      }`}
                    >
                      <span className="font-mono font-bold text-sm">
                        {node.value}
                      </span>
                    </div>
                    <div className="text-xs text-center mt-1 text-gray-500">
                      [{node.index}]
                    </div>

                    {/* Pointer indicators */}
                    {idx === currentStepData?.slowIndex && (
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                        <Turtle className="text-green-600" size={14} />
                      </div>
                    )}
                    {idx === currentStepData?.fastIndex && (
                      <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2">
                        <Rabbit className="text-purple-600" size={14} />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Cycle visualization */}
              {cyclePos >= 0 && (
                <div className="mt-4 p-2 bg-yellow-50 rounded">
                  <p className="text-xs text-yellow-800">
                    ⚡ Cycle: Last node ({list.length - 1}) points to node{" "}
                    {cyclePos}
                  </p>
                </div>
              )}
            </div>

            {/* Movement History */}
            {currentStepData?.iteration > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 bg-green-50 rounded">
                  <p className="font-medium text-green-700">Slow visited:</p>
                  <p className="text-green-600">
                    {currentStepData?.visitedBySlow
                      ? Array.from(currentStepData.visitedBySlow).join(" → ")
                      : ""}
                  </p>
                </div>
                <div className="p-2 bg-purple-50 rounded">
                  <p className="font-medium text-purple-700">Fast visited:</p>
                  <p className="text-purple-600">
                    {currentStepData?.visitedByFast
                      ? Array.from(currentStepData.visitedByFast).join(" → ")
                      : ""}
                  </p>
                </div>
              </div>
            )}

            {/* Result */}
            {currentStepData?.isFinal && (
              <div
                className={`mt-3 p-3 rounded ${
                  cycleDetected ? "bg-red-100" : "bg-green-100"
                }`}
              >
                <h4
                  className={`text-sm font-bold ${
                    cycleDetected ? "text-red-900" : "text-green-900"
                  }`}
                >
                  {cycleDetected ? "Cycle Detected!" : "No Cycle Found!"}
                </h4>
                <p
                  className={`text-xs mt-1 ${
                    cycleDetected ? "text-red-700" : "text-green-700"
                  }`}
                >
                  {cycleDetected
                    ? `The fast and slow pointers met at node ${currentStepData.slowIndex}`
                    : "Fast pointer reached the end of the list"}
                </p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep === 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>

              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                {isPlaying ? "Pause" : "Play"}
              </button>

              <button
                onClick={handleNextStep}
                disabled={currentStep === steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>

              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} />
                Reset
              </button>

              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="2000"
                  step="200"
                  value={2200 - speed}
                  onChange={(e) => setSpeed(2200 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>

            {/* Step indicator */}
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right side - Pseudo Code & Analysis */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              Floyd's Cycle Detection Algorithm
            </h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>

          {/* Algorithm Explanation */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">How It Works</h3>
            <ol className="space-y-1 text-xs text-gray-700">
              <li className="flex">
                <span className="font-medium mr-1">1.</span>
                <span>
                  Initialize two pointers: slow (tortoise) and fast (hare)
                </span>
              </li>
              <li className="flex">
                <span className="font-medium mr-1">2.</span>
                <span>Slow moves one step at a time</span>
              </li>
              <li className="flex">
                <span className="font-medium mr-1">3.</span>
                <span>Fast moves two steps at a time</span>
              </li>
              <li className="flex">
                <span className="font-medium mr-1">4.</span>
                <span>If there's a cycle, they will eventually meet</span>
              </li>
              <li className="flex">
                <span className="font-medium mr-1">5.</span>
                <span>If fast reaches null, there's no cycle</span>
              </li>
            </ol>
          </div>

          {/* Complexity Analysis */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Complexity Analysis</h3>
            <div className="space-y-2">
              <div>
                <h4 className="text-xs font-medium text-gray-700">
                  Time Complexity: O(n)
                </h4>
                <p className="text-xs text-gray-600">
                  In worst case, we visit each node at most twice
                </p>
              </div>
              <div>
                <h4 className="text-xs font-medium text-gray-700">
                  Space Complexity: O(1)
                </h4>
                <p className="text-xs text-gray-600">
                  Only using two pointers regardless of list size
                </p>
              </div>
            </div>
          </div>

          {/* Key Insights */}
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>Fast pointer covers distance twice as fast as slow</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  In a cycle, fast will catch up to slow like a race on a track
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Meeting point proves cycle existence, not necessarily cycle
                  start
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>Also known as "Tortoise and Hare" algorithm</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>More efficient than using a hash set (O(n) space)</span>
              </li>
            </ul>
          </div>

          {/* Visual Guide */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Pointer Movement</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Turtle className="text-green-600" size={20} />
                <div className="text-xs">
                  <span className="font-medium">Slow Pointer (Tortoise)</span>
                  <p className="text-gray-600">Moves 1 step per iteration</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Rabbit className="text-purple-600" size={20} />
                <div className="text-xs">
                  <span className="font-medium">Fast Pointer (Hare)</span>
                  <p className="text-gray-600">Moves 2 steps per iteration</p>
                </div>
              </div>
              <div className="mt-3 p-2 bg-red-50 rounded">
                <p className="text-xs text-red-800">
                  When both pointers meet at the same node, a cycle is
                  confirmed!
                </p>
              </div>
            </div>
          </div>

          {/* Mathematical Proof */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Why It Works</h3>
            <div className="text-xs text-gray-700 space-y-1">
              <p>
                If there's a cycle of length{" "}
                <span className="font-mono">L</span>:
              </p>
              <ul className="ml-4 space-y-0.5">
                <li>
                  • Slow enters cycle after <span className="font-mono">S</span>{" "}
                  steps
                </li>
                <li>
                  • Fast enters cycle after <span className="font-mono">S</span>{" "}
                  steps
                </li>
                <li>
                  • Fast is <span className="font-mono">L</span> steps ahead in
                  the cycle
                </li>
                <li>• Fast catches up 1 step per iteration</li>
                <li>
                  • They meet after <span className="font-mono">L</span>{" "}
                  iterations in cycle
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkedListCycleVisualizer;
